import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Field, Control, Input } from "rbx";
import moment from "moment";
import gql from "graphql-tag";
import { useQuery } from "react-apollo";
import { useDebouncedValue } from "../../../hooks";
import DataTable from "../../../components/DataTable";
import PageHeader from "../../../components/PageHeader";
import { ORDER_VIEW_COLUMNS } from "./columns";
import PrintBOL from "../../../components/PrintBOL";

import { ALL_ORDERS_VIEW_BY_PAGE_QUERY } from "../../../graphql/Orders/Orders.graphql";

import OrderFilters from "./OrderHistoryFilters";
import { getFilterState, saveFilterState, omit } from "../../../utils";

import OrderStatusFilter from "./OrderHistoryStatusFilter";
import Loading from "../../../components/Loading";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const DEFAULT_FILTERS = {};

function OrderHistoryPage() {
  const SystemCodes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order"],
        CodeName_in: ["OrderStatus"],
        CodeId_in: ["NB", "BP", "VD", "CAN"]
      }
    }
  });
  const history = useHistory();
  const INITIAL_FILTER_STATE = getFilterState("ORDER_HISTORY_PAGE_FILTERS", {
    ...DEFAULT_FILTERS
  });

  const [orderNumber, setOrderNumber] = useState("");
  const debouncedOrderNumber = useDebouncedValue(orderNumber, 500);

  const [filters, setFilters] = useState({ ...INITIAL_FILTER_STATE });
  const [codes, setCodes] = useState(null);

  useEffect(() => {
    if (Array.isArray(SystemCodes?.data?.SystemCodes) && !codes) {
      setCodes(
        SystemCodes.data.SystemCodes.map(({ RecId, CodeDescription }) => ({
          value: RecId,
          label: CodeDescription
        }))
      );
      if (
        SystemCodes?.data?.SystemCodes?.length > 0 &&
        !filters.StatusRecIdRef_in
      ) {
        setFilters({
          ...filters,
          StatusRecIdRef_in: SystemCodes.data.SystemCodes.map(f => f.RecId)
        });
      }
    }
  }, [filters, INITIAL_FILTER_STATE, SystemCodes, codes]);

  useEffect(() => {
    setFilters(prev =>
      debouncedOrderNumber
        ? { ...prev, OrderNumber: parseInt(debouncedOrderNumber, 10) }
        : omit(prev, ["OrderNumber"])
    );
  }, [debouncedOrderNumber]);

  const handleSetFilters = inputs => {
    setFilters({ ...inputs });
  };
  const resetFilters = () => handleSetFilters({ ...DEFAULT_FILTERS });

  useEffect(() => {
    saveFilterState("ORDER_HISTORY_PAGE_FILTERS", filters);
  }, [filters]);

  // this enables us to use to / from === same day but still query a range
  const parseFilters = input => {
    const where = { ...input };
    if (input.PickupDate_gt) {
      where.PickupDate_gt = moment(input.PickupDate_gt)
        .startOf("day")
        .toISOString();
    }
    if (input.PickupDate_lt) {
      where.PickupDate_lt = moment(input.PickupDate_lt)
        .endOf("day")
        .toISOString();
    }
    if (input.OrderDate_gt) {
      where.OrderDate_gt = moment(input.OrderDate_gt)
        .startOf("day")
        .toISOString();
    }
    if (input.OrderDate_lt) {
      where.OrderDate_lt = moment(input.OrderDate_lt)
        .endOf("day")
        .toISOString();
    }
    return where;
  };

  if (!filters.StatusRecIdRef_in || !codes) {
    return <Loading />;
  }

  return (
    <React.Fragment>
      <PageHeader
        FilterComponent={OrderFilters}
        columns={ORDER_VIEW_COLUMNS}
        filters={filters}
        name="VW_OrdersByPage"
        query={ALL_ORDERS_VIEW_BY_PAGE_QUERY}
        resetFilters={resetFilters}
        setFilters={handleSetFilters}
        title="Order History"
        onAddClick={() => {
          history.push("/dispatch/orders/new");
        }}
      >
        <Control>
          <PrintBOL filters={filters} />
        </Control>
      </PageHeader>
      <Field kind="group">
        <Control>
          <Input
            placeholder="Order Number"
            style={{ height: 38 }}
            type="text"
            value={orderNumber}
            onChange={e => {
              setOrderNumber(
                /^(\s*|\d+)$/.test(e.target.value)
                  ? e.target.value
                  : orderNumber
              );
            }}
          />
        </Control>
        <Control expanded>
          <OrderStatusFilter
            filters={filters}
            handleSetFilters={handleSetFilters}
            options={codes}
          />
        </Control>
      </Field>
      <DataTable
        isPaginated
        columns={ORDER_VIEW_COLUMNS}
        dataQuery={ALL_ORDERS_VIEW_BY_PAGE_QUERY}
        hiddenColumns={["StatusIdListOrder"]}
        initialSortBy={[
          { id: "StatusIdListOrder", desc: false },
          {
            id: "PickupDate",
            desc: false
          },
          { id: "OrderNumber", desc: false }
        ]}
        name="VW_OrdersByPage"
        where={parseFilters(filters)}
        onRowClick={({ RecId }) =>
          history.push(`/dispatch/orders/${RecId}/summary`)
        }
      />
    </React.Fragment>
  );
}
OrderHistoryPage.propTypes = {};
export default OrderHistoryPage;
